/**
 * Add Customer Service Modal
 *
 * Allows manually adding services to existing customers
 * Pulls plans from /settings/plans (service_plans collection) for Starlink, NBN, 4G, VoIP
 * NBN requires selecting a technology type first, which filters available plans
 */

import { getTenantIdSync } from '@/lib/tenant';
import { useState, useMemo } from 'react';
import { X, Wifi, Save, Plus, Package, Trash2, Smartphone, Router, Edit2, MapPin } from 'lucide-react';
import { useModalAccessibility } from '@/hooks/useModalAccessibility';
import { CustomerService, ServicePlan } from '../types/schema';
import { createCustomerService, createBundledServices } from '../services/customer-service.service';
import { findPossibleDuplicateServices } from '../services/customer-service-activation.service';
import {
  ServiceAddressPicker,
  ServiceAddressSelection,
  commitServiceAddressToBook,
} from './ServiceAddressPicker';
import { useServicePlans } from '../hooks/useServicePlans';
import { useAllServicePlans } from '@/hooks/useServicePlans';
import { ServicePlanModal } from '@/components/service-plans/ServicePlanModal';
import { useTenant } from '@/hooks/useTenant';
import { getTenantTaxRate } from '@/utils/taxRate';
import { formatCurrency, addGST } from '@/utils/pricing';
import { exGstToIncString, incStringToExGst } from '@/components/common/IncGstPriceInput';
import { planLabelWithSpeed } from '@/utils/planSpeed';
import { detectPlanTechnologies, planMatchesNbnTechnology } from '@/utils/nbnTechnology';

interface AddCustomerServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  customerAddress?: string;
  onServiceAdded?: () => void;
}

// NBN technology types and their max speed tiers
const NBN_TECHNOLOGIES = [
  { value: 'FTTN', label: 'FTTN', description: 'Fibre to the Node', maxSpeed: 100 },
  { value: 'FTTB', label: 'FTTB', description: 'Fibre to the Building', maxSpeed: 100 },
  { value: 'FTTC', label: 'FTTC', description: 'Fibre to the Curb', maxSpeed: 250 },
  { value: 'FTTP', label: 'FTTP', description: 'Fibre to the Premises', maxSpeed: 1000 },
  // Fixed Wireless cap lifted to 250 — NBN's upgraded Fixed Wireless plans now
  // run above the old 100 Mbps ceiling, which was hiding faster FW plans from
  // the picker (they only showed under FTTP). This is a soft guardrail, not a
  // hard limit; raise it further if a faster FW tier is offered.
  { value: 'FW', label: 'FW', description: 'Fixed Wireless', maxSpeed: 250 },
  { value: 'HFC', label: 'HFC', description: 'Hybrid Fibre Coaxial', maxSpeed: 250 },
] as const;

// Service types that pull plans from Firestore
const PLAN_BASED_TYPES = ['Starlink', 'NBN', 'OptiComm', '4G/5G', 'VoIP'];

export function AddCustomerServiceModal({
  isOpen,
  onClose,
  customerId,
  customerName,
  customerAddress,
  onServiceAdded,
}: AddCustomerServiceModalProps) {
  const { tenant } = useTenant();
  const taxRate = getTenantTaxRate(tenant);
  const [serviceType, setServiceType] = useState<CustomerService['serviceType']>('Starlink');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Plan modal (inline add/edit)
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planToEdit, setPlanToEdit] = useState<ServicePlan | null>(null);

  // Service address — pick from the customer's saved addresses or enter a new
  // one. Seeds from the customer's primary address; the picker matches it to a
  // saved-address card when possible.
  const [addressSel, setAddressSel] = useState<ServiceAddressSelection>({
    address: customerAddress || '',
    city: '',
    state: '',
    zipCode: '',
    saveToBook: false,
  });

  // Plan selection (from service_plans collection)
  const [selectedPlanId, setSelectedPlanId] = useState('');

  // Starlink metadata
  const [kitNumber, setKitNumber] = useState('');
  const [starlinkAccountEmail, setStarlinkAccountEmail] = useState('');
  const [starlinkAccountNumber, setStarlinkAccountNumber] = useState('');
  const [starlinkModel, setStarlinkModel] = useState<'Standard' | 'Mini' | ''>('');

  // 4G/5G metadata
  const [carrier, setCarrier] = useState<'Telstra' | 'Optus' | ''>('');
  const [simNumber, setSimNumber] = useState('');
  const [serviceNumber, setServiceNumber] = useState('');

  // NBN metadata
  const [nbnServiceId, setNbnServiceId] = useState('');
  const [nbnLocationId, setNbnLocationId] = useState('');
  const [technology, setTechnology] = useState('');

  // OptiComm metadata (private fibre wholesaler — FTTP only, so no tech tiers)
  const [opticommServiceId, setOpticommServiceId] = useState('');
  const [opticommOrderNumber, setOpticommOrderNumber] = useState('');

  // WiFi Extension metadata
  const [apCount, setApCount] = useState('');
  const [apBrand, setApBrand] = useState('');
  const [apModel, setApModel] = useState('');
  const [apSerials, setApSerials] = useState('');
  const [switchModel, setSwitchModel] = useState('');

  // Smart Home metadata
  const [hubBrand, setHubBrand] = useState('');
  const [hubModel, setHubModel] = useState('');
  const [hubSerial, setHubSerial] = useState('');
  const [deviceCount, setDeviceCount] = useState('');

  // Security metadata
  const [cameraBrand, setCameraBrand] = useState('');
  const [cameraModel, setCameraModel] = useState('');
  const [cameraCount, setCameraCount] = useState('');
  const [nvrModel, setNvrModel] = useState('');
  const [nvrSerial, setNvrSerial] = useState('');
  const [panelSerial, setPanelSerial] = useState('');
  const [sensorCount, setSensorCount] = useState('');
  const [doorbellModel, setDoorbellModel] = useState('');

  // VoIP metadata
  const [sasbossCustomerName, setSasbossCustomerName] = useState('');
  const [mainNumber, setMainNumber] = useState('');
  const [handsetCount, setHandsetCount] = useState('');
  const [handsetLocations, setHandsetLocations] = useState('');
  const [handsetBrand, setHandsetBrand] = useState('');
  const [byoHandsets, setByoHandsets] = useState(false);
  const [portingRequired, setPortingRequired] = useState(false);
  const [numbersToPort, setNumbersToPort] = useState('');
  const [portingStatus, setPortingStatus] = useState<'' | 'pending' | 'submitted' | 'approved' | 'completed'>('');
  const [voipComplexity, setVoipComplexity] = useState<'' | 'Simple' | 'Complex'>('');
  const [extensionCount, setExtensionCount] = useState('');
  const [voicemailEnabled, setVoicemailEnabled] = useState(false);
  const [autoAttendant, setAutoAttendant] = useState(false);
  const [callFlowNotes, setCallFlowNotes] = useState('');

  // Common metadata
  const [accountNumber, setAccountNumber] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [notes, setNotes] = useState('');

  // Custom plan name + price overrides — used for bundles, legacy/grandfathered
  // pricing, retention deals, etc. Empty = use the plan's default values.
  // The price override is logged to priceHistory for audit when service is created.
  //
  // GST BASIS: `customPrice` and `bundlePrice` below hold the INC-GST string the
  // office types (the price the customer actually pays). Both are converted to
  // the ex-GST basis before anything is stored — `monthlyPrice` / `sellPrice` are
  // ex-GST everywhere, because Utilmate and Xero bill off that.
  const [customPlanName, setCustomPlanName] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [customPriceReason, setCustomPriceReason] = useState('');

  // Bundle mode (V107.52.34) — alternative submission path that creates N
  // recurring services sharing one bundleId, one bundle name, and one combined
  // monthlyPrice (carried by the primary; other members store 0). Per-service
  // metadata is left blank and filled in afterwards by editing each member.
  const [bundleMode, setBundleMode] = useState(false);
  const [bundleName, setBundleName] = useState('');
  const [bundlePrice, setBundlePrice] = useState('');
  const [bundleReason, setBundleReason] = useState('');
  // Ex-GST values derived from the inc-GST inputs above — NaN while blank or
  // unparseable, which every consumer already treats as "not set".
  const customPriceExGst = incStringToExGst(customPrice, taxRate);
  const bundlePriceExGst = incStringToExGst(bundlePrice, taxRate);

  type BundleRow = { rowId: string; serviceType: CustomerService['serviceType']; planId: string };
  const [bundleRows, setBundleRows] = useState<BundleRow[]>([
    { rowId: 'r1', serviceType: 'NBN', planId: '' },
    { rowId: 'r2', serviceType: 'VoIP', planId: '' },
  ]);

  // All plans across all service types — used to populate bundle row plan dropdowns
  const { plans: bundleAllPlans } = useAllServicePlans();

  // Fetch plans from Firestore based on service type. The "4G/5G" UI option
  // covers two Firestore serviceType values — '4G Mobile' (SIM-based) and
  // '4G Data' (router-based) — so we query both and visually tag each plan
  // in the list. See Mobile/Data icon next to plan name.
  const planServiceType = useMemo(() => {
    if (serviceType === '4G/5G') return ['4G Mobile', '4G Data'] as const;
    if (PLAN_BASED_TYPES.includes(serviceType)) return serviceType;
    return undefined;
  }, [serviceType]);

  const { plans: allPlans, loading: plansLoading } = useServicePlans({
    serviceType: planServiceType as ServicePlan['serviceType'] | ServicePlan['serviceType'][] | undefined,
    activeOnly: true,
    sortByPromoted: true,
  });

  // For NBN, align plans to the selected technology. No technology picked =
  // "All" (every plan shows). Once picked, technology-tagged plans (explicit
  // tag or name/alias keyword, e.g. "Fixed Wireless") only show for their own
  // technology; generic plans fall back to the speed cap.
  const availablePlans = useMemo(() => {
    if (serviceType !== 'NBN' || !technology) return allPlans;

    const techConfig = NBN_TECHNOLOGIES.find((t) => t.value === technology);
    if (!techConfig) return allPlans;

    return allPlans.filter((plan) => planMatchesNbnTechnology(plan, techConfig.value, techConfig.maxSpeed));
  }, [allPlans, serviceType, technology]);

  const selectedPlan = useMemo(() => {
    return availablePlans.find((p) => p.id === selectedPlanId) || null;
  }, [availablePlans, selectedPlanId]);

  const isPlanBased = PLAN_BASED_TYPES.includes(serviceType);

  const resetForm = () => {
    setAddressSel({ address: customerAddress || '', city: '', state: '', zipCode: '', saveToBook: false });
    setSelectedPlanId('');
    setKitNumber('');
    setStarlinkAccountEmail('');
    setStarlinkAccountNumber('');
    setStarlinkModel('');
    setCarrier('');
    setSimNumber('');
    setNbnServiceId('');
    setNbnLocationId('');
    setTechnology('');
    setOpticommServiceId('');
    setOpticommOrderNumber('');
    setApCount('');
    setApSerials('');
    setHubSerial('');
    setDeviceCount('');
    setPanelSerial('');
    setCameraCount('');
    setSensorCount('');
    setSasbossCustomerName('');
    setMainNumber('');
    setHandsetCount('');
    setHandsetLocations('');
    setHandsetBrand('');
    setByoHandsets(false);
    setPortingRequired(false);
    setNumbersToPort('');
    setPortingStatus('');
    setVoipComplexity('');
    setExtensionCount('');
    setVoicemailEnabled(false);
    setAutoAttendant(false);
    setCallFlowNotes('');
    setAccountNumber('');
    setContractEndDate('');
    setNotes('');
    setCustomPlanName('');
    setCustomPrice('');
    setCustomPriceReason('');
    setBundleMode(false);
    setBundleName('');
    setBundlePrice('');
    setBundleReason('');
    setBundleRows([
      { rowId: 'r1', serviceType: 'NBN', planId: '' },
      { rowId: 'r2', serviceType: 'VoIP', planId: '' },
    ]);
  };

  const handleServiceTypeChange = (type: CustomerService['serviceType']) => {
    setServiceType(type);
    setSelectedPlanId('');
    setTechnology('');
    // Switching service type invalidates any plan-specific overrides
    setCustomPlanName('');
    setCustomPrice('');
    setCustomPriceReason('');
  };

  const handleTechnologyChange = (tech: string) => {
    setTechnology(tech);
    setSelectedPlanId(''); // Reset plan when tech changes
    setCustomPlanName('');
    setCustomPrice('');
    setCustomPriceReason('');
  };

  // Picking a technology-tagged plan while on "All" auto-selects its
  // technology, so the required NBN tech field fills itself when the plan
  // already implies it (e.g. "NBN Fixed Wireless Plus" → FW).
  const handleSelectPlan = (plan: ServicePlan) => {
    setSelectedPlanId(plan.id!);
    if (serviceType === 'NBN' && !technology) {
      const detected = detectPlanTechnologies(plan);
      if (detected.length === 1) setTechnology(detected[0]);
    }
  };

  const handleAddService = async () => {
    if (!customerId) {
      alert('Customer ID is required');
      return;
    }

    // Bundle mode takes a completely different submission path — multi-service
    // atomic write via createBundledServices(). All single-service validation
    // below is skipped.
    if (bundleMode) {
      if (!bundleName.trim()) {
        alert('Bundle name is required.');
        return;
      }
      // `bundlePrice` is typed INC GST; convert to the ex-GST basis we store.
      const parsedBundlePrice = incStringToExGst(bundlePrice, taxRate);
      if (Number.isNaN(parsedBundlePrice) || parsedBundlePrice < 0) {
        alert('Bundle monthly price must be a non-negative number.');
        return;
      }
      if (bundleRows.length < 2) {
        alert('A bundle needs at least 2 services.');
        return;
      }
      if (bundleRows.some((r) => !r.planId)) {
        alert('Pick a plan for every service in the bundle.');
        return;
      }

      setIsSubmitting(true);
      try {
        await createBundledServices(customerId, customerName, {
          name: bundleName.trim(),
          monthlyPrice: parsedBundlePrice,
          reason: bundleReason.trim() || undefined,
          services: bundleRows.map((r) => {
            const p = bundleAllPlans.find((pl) => pl.id === r.planId);
            return {
              serviceType: r.serviceType,
              planId: r.planId,
              planSellPrice: p?.sellPrice,
            };
          }),
          address: addressSel.address || undefined,
          city: addressSel.city || undefined,
          state: addressSel.state || undefined,
          zipCode: addressSel.zipCode || undefined,
          notes: notes || undefined,
        });

        await commitServiceAddressToBook(customerId, addressSel, bundleName.trim() || 'Bundle');

        alert(`Bundle "${bundleName.trim()}" added (${bundleRows.length} services). Edit each service afterwards to fill in kit numbers / account IDs.`);
        resetForm();
        onServiceAdded?.();
        onClose();
      } catch (error) {
        console.error('Error creating bundle:', error);
        alert(`Failed to create bundle: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Validation
    if (serviceType === 'Starlink' && !kitNumber.trim()) {
      alert('Kit Number is required for Starlink services');
      return;
    }

    if (isPlanBased && !selectedPlanId) {
      alert('Please select a plan');
      return;
    }

    if (serviceType === 'NBN' && !technology) {
      alert('Please select an NBN technology type');
      return;
    }

    if (serviceType === '4G/5G') {
      if (!carrier) {
        alert('Please select a carrier (Telstra or Optus)');
        return;
      }
      if (!simNumber.trim()) {
        alert('SIM Number is required for 4G/5G services');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Build metadata based on service type
      const metadata: CustomerService['metadata'] = {};

      if (serviceType === 'Starlink') {
        if (kitNumber) metadata.kitNumber = kitNumber;
        if (starlinkAccountEmail) metadata.starlinkAccountEmail = starlinkAccountEmail;
        if (starlinkAccountNumber) metadata.starlinkAccountNumber = starlinkAccountNumber.trim();
        if (starlinkModel) metadata.starlinkModel = starlinkModel;
      } else if (serviceType === '4G/5G') {
        if (carrier) metadata.carrier = carrier;
        if (simNumber) metadata.simNumber = simNumber;
        if (serviceNumber.trim()) (metadata as { serviceNumber?: string }).serviceNumber = serviceNumber.trim();
      } else if (serviceType === 'NBN') {
        if (nbnServiceId) metadata.nbnServiceId = nbnServiceId;
        if (nbnLocationId) metadata.nbnLocationId = nbnLocationId;
        if (technology) metadata.technology = technology;
      } else if (serviceType === 'OptiComm') {
        if (opticommServiceId) metadata.opticommServiceId = opticommServiceId.trim();
        if (opticommOrderNumber) metadata.opticommOrderNumber = opticommOrderNumber.trim();
      } else if (serviceType === 'WiFi Extension') {
        if (apCount) metadata.accessPointCount = parseInt(apCount);
        if (apBrand) metadata.accessPointBrand = apBrand;
        if (apModel) metadata.accessPointModel = apModel;
        if (apSerials) metadata.accessPointSerials = apSerials.split(',').map((s) => s.trim());
        if (switchModel) metadata.switchModel = switchModel;
      } else if (serviceType === 'Smart Home') {
        if (hubBrand) metadata.hubBrand = hubBrand;
        if (hubModel) metadata.hubModel = hubModel;
        if (hubSerial) metadata.hubSerialNumber = hubSerial;
        if (deviceCount) metadata.deviceCount = parseInt(deviceCount);
      } else if (serviceType === 'Security') {
        if (cameraBrand) metadata.cameraBrand = cameraBrand;
        if (cameraModel) metadata.cameraModel = cameraModel;
        if (cameraCount) metadata.cameraCount = parseInt(cameraCount);
        if (nvrModel) metadata.nvrModel = nvrModel;
        if (nvrSerial) metadata.nvrSerial = nvrSerial;
        if (panelSerial) metadata.panelSerialNumber = panelSerial;
        if (sensorCount) metadata.sensorCount = parseInt(sensorCount);
        if (doorbellModel) metadata.doorbellModel = doorbellModel;
      } else if (serviceType === 'VoIP') {
        if (sasbossCustomerName) metadata.sasbossCustomerName = sasbossCustomerName;
        if (mainNumber) metadata.mainNumber = mainNumber;
        if (handsetCount) metadata.handsetCount = parseInt(handsetCount);
        if (handsetLocations) metadata.handsetLocations = handsetLocations;
        if (handsetBrand) metadata.handsetBrand = handsetBrand;
        metadata.byoHandsets = byoHandsets;
        metadata.portingRequired = portingRequired;
        if (portingRequired && numbersToPort.trim()) {
          metadata.numbersToPort = numbersToPort
            .split(/[,\n]/)
            .map((n) => n.trim())
            .filter(Boolean);
        }
        if (portingStatus) metadata.portingStatus = portingStatus;
        if (voipComplexity) metadata.complexity = voipComplexity;
        if (extensionCount) metadata.extensionCount = parseInt(extensionCount);
        metadata.voicemailEnabled = voicemailEnabled;
        metadata.autoAttendant = autoAttendant;
        if (callFlowNotes) metadata.callFlowNotes = callFlowNotes;
      }

      if (accountNumber) metadata.accountNumber = accountNumber;
      if (contractEndDate) metadata.contractEndDate = contractEndDate;

      // Determine billing type and address binding
      const isInstallation = ['WiFi Extension', 'Smart Home', 'Security', 'IT'].includes(serviceType);

      // Resolve effective plan name + price. User-entered overrides win; if both
      // fields are blank we fall back to the plan's defaults. The plan id is
      // always preserved so we can trace back to the source plan even when
      // displaying a bundle/custom name.
      const planDefaultName = selectedPlan?.displayName || selectedPlan?.planName;
      const planDefaultPrice = selectedPlan?.sellPrice;
      const trimmedCustomName = customPlanName.trim();
      const trimmedCustomPriceStr = customPrice.trim();
      // `customPrice` is typed INC GST; monthlyPrice is STORED ex GST.
      const parsedCustomPrice = incStringToExGst(customPrice, taxRate);

      // Validate price input if user filled it in
      if (trimmedCustomPriceStr !== '' && (Number.isNaN(parsedCustomPrice) || parsedCustomPrice < 0)) {
        alert('Custom price must be a non-negative number.');
        setIsSubmitting(false);
        return;
      }

      const planName = trimmedCustomName || planDefaultName || undefined;
      const monthlyPrice = !Number.isNaN(parsedCustomPrice) ? parsedCustomPrice : (planDefaultPrice || undefined);
      const planId = selectedPlan?.id || undefined;

      // Build the priceOverride hint for createCustomerService — only when we
      // actually have a plan to compare against AND the price differs from it.
      const initialPriceOverride =
        planDefaultPrice != null && typeof monthlyPrice === 'number'
          && Math.round(monthlyPrice * 100) !== Math.round(planDefaultPrice * 100)
          ? {
              planSellPrice: planDefaultPrice,
              reason: customPriceReason.trim() || (
                trimmedCustomName
                  ? `Bundle pricing — "${trimmedCustomName}"`
                  : undefined
              ),
            }
          : undefined;

      // Duplicate guard. Every deal-driven creation path dedups; this one had
      // no check at all, so a service auto-created from a deal at install
      // completion (no address, deal-linked) could be silently re-added by
      // hand under the retail plan name. Warn, don't block — a genuine second
      // service at the same site is legitimate and the office knows which is
      // which. Best-effort: a failed lookup must not stop the add.
      const possibleDuplicates = await findPossibleDuplicateServices(
        customerId,
        serviceType,
        addressSel.address || undefined,
      ).catch((err) => {
        console.warn('[AddService] Duplicate pre-check failed — continuing:', err);
        return [];
      });

      if (possibleDuplicates.length > 0) {
        const lines = possibleDuplicates.map((s) => {
          const label = s.planName || s.serviceType;
          const source = s.dealId ? ` — from deal ${s.dealId.slice(0, 8)}` : '';
          return `  • ${label} — ${s.status}${source}`;
        });
        const proceed = window.confirm(
          `${customerName} already has ${possibleDuplicates.length} live ${serviceType} ` +
            `service${possibleDuplicates.length > 1 ? 's' : ''} at this address:\n\n` +
            `${lines.join('\n')}\n\n` +
            'Adding another creates a second record for what may be the same service. ' +
            'If you meant to correct the existing one, cancel and use Edit instead.\n\n' +
            'Add it anyway?',
        );
        if (!proceed) {
          setIsSubmitting(false);
          return;
        }
      }

      await createCustomerService(
        customerId,
        customerName,
        serviceType,
        {
          billingType: isInstallation ? 'one-time' : 'recurring',
          addressBound: isInstallation,
          monthlyPrice,
          planName,
          planId,
          initialPriceOverride,
          metadata,
          address: addressSel.address || undefined,
          city: addressSel.city || undefined,
          state: addressSel.state || undefined,
          zipCode: addressSel.zipCode || undefined,
          notes: notes || undefined,
          installationNotes: 'Manually added to existing customer',
        }
      );

      // Fold a newly-entered address into the customer's saved addresses
      // (dedup-safe, best-effort — runs after the service is created).
      await commitServiceAddressToBook(customerId, addressSel, serviceType);

      alert(`${serviceType} service added successfully!`);
      resetForm();
      onServiceAdded?.();
      onClose();
    } catch (error) {
      console.error('Error adding service:', error);
      alert('Failed to add service. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalRef = useModalAccessibility({ isOpen, onClose });

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" aria-label="Add Service">
      <div
        ref={modalRef}
        className="bg-gradient-to-br from-brand-primary to-brand-dark border border-brand-secondary/30 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand-secondary/20 to-transparent backdrop-blur-sm border-b border-white/10 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-secondary to-yellow-600 flex items-center justify-center shadow-lg">
              <Plus className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Add Service</h2>
              <p className="text-sm text-slate-400">{customerName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Mode toggle: Single Service vs Bundle */}
          <div className="grid grid-cols-2 gap-2 bg-white/5 border border-white/10 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setBundleMode(false)}
              className={`py-2 rounded-md text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                !bundleMode
                  ? 'bg-brand-secondary text-white shadow'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Plus className="w-4 h-4" />
              Single Service
            </button>
            <button
              type="button"
              onClick={() => setBundleMode(true)}
              className={`py-2 rounded-md text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                bundleMode
                  ? 'bg-amber-500 text-white shadow'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Package className="w-4 h-4" />
              Bundle (multi-service)
            </button>
          </div>

          {/* Bundle mode form — replaces the single-service form when active */}
          {bundleMode && (
            <div className="space-y-5">
              <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Package className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-slate-300">
                    <span className="text-amber-300 font-semibold">Bundle mode</span> creates one
                    service per row, all sharing this bundle's name and ID. The combined monthly
                    price sits on the first service; the others are recorded at $0/mo so MRR sums
                    correctly. <span className="text-slate-400">Service-specific details (kit numbers,
                    account IDs, etc.) can be filled in afterwards by editing each service.</span>
                  </div>
                </div>
              </div>

              {/* Bundle name + price */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Bundle name *
                  </label>
                  <input
                    type="text"
                    value={bundleName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBundleName(e.target.value)}
                    placeholder="e.g. Triple Play Bundle"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Bundle monthly price (AUD inc GST) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={bundlePrice}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBundlePrice(e.target.value)}
                      placeholder="0.00"
                      aria-label="Bundle monthly price including GST"
                      className="w-full pl-7 pr-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors text-sm"
                    />
                  </div>
                  {/* Ex-GST shadow — typed inc GST, stored ex GST. Re-derived from
                      the stored basis so it matches what will actually bill. */}
                  {!Number.isNaN(bundlePriceExGst) && bundlePriceExGst >= 0 && (
                    <p className="mt-1 text-[11px] text-slate-400">
                      Billed as {formatCurrency(bundlePriceExGst, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo ex GST
                    </p>
                  )}
                </div>
              </div>

              {/* Services in this bundle */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-white">
                    Services in this bundle <span className="text-slate-400 font-normal">({bundleRows.length})</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setBundleRows((rows) => [
                      ...rows,
                      { rowId: `r${Date.now()}`, serviceType: 'NBN', planId: '' },
                    ])}
                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 transition-colors flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add service
                  </button>
                </div>

                <div className="space-y-2">
                  {bundleRows.map((row, idx) => {
                    // The '4G/5G' UI option spans both '4G Mobile' and '4G Data'
                    // Firestore types — match either when that row is picked.
                    const planSvcTypes = row.serviceType === '4G/5G'
                      ? ['4G Mobile', '4G Data']
                      : [row.serviceType];
                    const plansForRow = bundleAllPlans.filter(
                      (p) => planSvcTypes.includes(p.serviceType) && p.isActive !== false
                    );
                    return (
                      <div
                        key={row.rowId}
                        className="flex items-center gap-2 p-2 bg-white/5 border border-white/10 rounded-lg"
                      >
                        <span className="w-6 h-6 flex-shrink-0 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <select
                          value={row.serviceType}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            const newType = e.target.value as CustomerService['serviceType'];
                            setBundleRows((rows) => rows.map((r) =>
                              r.rowId === row.rowId ? { ...r, serviceType: newType, planId: '' } : r
                            ));
                          }}
                          className="px-2 py-1.5 bg-white/10 border border-white/20 rounded-md text-white text-sm focus:outline-none focus:border-amber-400"
                        >
                          <option value="NBN">NBN</option>
                          <option value="Starlink">Starlink</option>
                          <option value="4G/5G">4G/5G</option>
                          <option value="VoIP">VoIP</option>
                        </select>
                        <select
                          value={row.planId}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            const newPlanId = e.target.value;
                            setBundleRows((rows) => rows.map((r) =>
                              r.rowId === row.rowId ? { ...r, planId: newPlanId } : r
                            ));
                          }}
                          className="flex-1 min-w-0 px-2 py-1.5 bg-white/10 border border-white/20 rounded-md text-white text-sm focus:outline-none focus:border-amber-400"
                        >
                          <option value="">— Pick a plan —</option>
                          {plansForRow.map((p) => {
                            // <option> can't render Lucide icons — emoji prefix
                            // distinguishes Mobile (SIM) vs Data (router) when
                            // the row is the combined "4G/5G" type.
                            const prefix = p.serviceType === '4G Mobile'
                              ? '📱 '
                              : p.serviceType === '4G Data'
                                ? '📶 '
                                : '';
                            return (
                              <option key={p.id} value={p.id}>
                                {prefix}{planLabelWithSpeed(p)} ({formatCurrency(addGST(p.sellPrice), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo inc GST)
                              </option>
                            );
                          })}
                        </select>
                        <button
                          type="button"
                          onClick={() => setBundleRows((rows) => rows.filter((r) => r.rowId !== row.rowId))}
                          disabled={bundleRows.length <= 2}
                          className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={bundleRows.length <= 2 ? 'A bundle needs at least 2 services' : 'Remove service'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Optional reason */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  Reason (saved to audit log)
                </label>
                <input
                  type="text"
                  value={bundleReason}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBundleReason(e.target.value)}
                  placeholder={bundleName.trim() ? `Bundle pricing — "${bundleName.trim()}"` : 'e.g. Bundle pricing'}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors text-sm"
                />
              </div>

              {/* Bundle preview */}
              {bundleName.trim() && bundlePrice.trim() && (() => {
                // Ex-GST basis — the input is inc GST, everything compared
                // against it (plan sellPrice) is stored ex GST.
                const parsed = bundlePriceExGst;
                if (Number.isNaN(parsed) || parsed < 0) return null;
                const planSum = bundleRows.reduce((sum, r) => {
                  const p = bundleAllPlans.find((pl) => pl.id === r.planId);
                  return sum + (p?.sellPrice || 0);
                }, 0);
                const diff = parsed - planSum;
                return (
                  <div className="p-3 rounded-lg border bg-amber-500/10 border-amber-500/40">
                    <div className="flex items-center gap-2 mb-1">
                      <Package className="w-4 h-4 text-amber-300" />
                      <span className="text-sm font-medium text-amber-300">Bundle preview</span>
                    </div>
                    <div className="text-white font-semibold">{bundleName.trim()}</div>
                    {/* Prices grossed up to inc GST — bundle price + plan sellPrices are stored ex GST */}
                    <div className="text-sm text-slate-300">{formatCurrency(addGST(parsed), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo inc GST across {bundleRows.length} services</div>
                    {planSum > 0 && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        Sum of plans: {formatCurrency(addGST(planSum), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo inc GST · Bundle is{' '}
                        <span className={diff < 0 ? 'text-amber-300' : diff > 0 ? 'text-emerald-400' : ''}>
                          {diff === 0 ? 'same as plan total' : `${diff < 0 ? 'cheaper by' : 'more by'} ${formatCurrency(addGST(Math.abs(diff)), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} inc GST`}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Address (shared across the bundle) */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  Service Address (shared)
                </label>
                <ServiceAddressPicker
                  customerId={customerId}
                  value={addressSel}
                  onChange={setAddressSel}
                  autoSelectPrimary
                  hint="Shared across every service in this bundle."
                />
              </div>

              {/* Bundle-mode actions */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddService}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Package className="w-4 h-4" />
                  {isSubmitting ? 'Creating bundle...' : `Create Bundle (${bundleRows.length} services)`}
                </button>
              </div>
            </div>
          )}

          {/* Single-service form (the existing flow) — hidden in bundle mode */}
          {!bundleMode && (<>
          {/* Service Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Service Type *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(['Starlink', 'NBN', 'OptiComm', '4G/5G', 'VoIP', 'WiFi Extension', 'Smart Home', 'Security', 'IT'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleServiceTypeChange(type)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                    serviceType === type
                      ? 'bg-brand-secondary text-white shadow-lg scale-105'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            {/* "Other" demoted to a smaller secondary chip */}
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => handleServiceTypeChange('Other')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  serviceType === 'Other'
                    ? 'bg-slate-500/60 text-white border border-slate-400'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10'
                }`}
              >
                Other…
              </button>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-3">
            <label className="block text-sm font-semibold text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-brand-secondary" />
              Service Address
            </label>
            <ServiceAddressPicker
              customerId={customerId}
              value={addressSel}
              onChange={setAddressSel}
              autoSelectPrimary
            />
          </div>

          {/* NBN Technology Type — filters the plan list; also captured on the service */}
          {serviceType === 'NBN' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">NBN Technology *</h3>
              <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
                <button
                  type="button"
                  onClick={() => handleTechnologyChange('')}
                  className={`px-3 py-3 rounded-lg font-semibold transition-all text-sm text-center ${
                    !technology
                      ? 'bg-blue-500 text-white shadow-lg border-2 border-blue-400'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                  }`}
                >
                  <div className="font-bold">All</div>
                  <div className="text-[10px] opacity-70 mt-0.5">Every plan</div>
                </button>
                {NBN_TECHNOLOGIES.map((tech) => (
                  <button
                    key={tech.value}
                    type="button"
                    onClick={() => handleTechnologyChange(tech.value)}
                    className={`px-3 py-3 rounded-lg font-semibold transition-all text-sm text-center ${
                      technology === tech.value
                        ? 'bg-blue-500 text-white shadow-lg border-2 border-blue-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    <div className="font-bold">{tech.label}</div>
                    <div className="text-[10px] opacity-70 mt-0.5">{tech.description}</div>
                  </button>
                ))}
              </div>
              {technology ? (
                <p className="text-xs text-slate-400">
                  Max speed: {NBN_TECHNOLOGIES.find((t) => t.value === technology)?.maxSpeed} Mbps — {availablePlans.length} plan{availablePlans.length !== 1 ? 's' : ''} available
                </p>
              ) : (
                <p className="text-xs text-slate-400">
                  Showing all technologies — pick one to filter plans (required before saving; picking a tagged plan sets it for you).
                </p>
              )}
            </div>
          )}

          {/* Plan Picker (for plan-based service types) */}
          {isPlanBased && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Select Plan *</h3>
                <button
                  type="button"
                  onClick={() => { setPlanToEdit(null); setPlanModalOpen(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Plan
                </button>
              </div>
              {plansLoading ? (
                <div className="text-slate-400 text-sm py-4 text-center">Loading plans...</div>
              ) : availablePlans.length === 0 ? (
                <div className="text-center py-6 space-y-2">
                  <p className="text-slate-400 text-sm">No active plans found for this service type.</p>
                  <button
                    type="button"
                    onClick={() => { setPlanToEdit(null); setPlanModalOpen(true); }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Create a Plan
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {availablePlans.map((plan) => (
                    <div key={plan.id} className="relative group">
                      <button
                        type="button"
                        onClick={() => handleSelectPlan(plan)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-all border ${
                          selectedPlanId === plan.id
                            ? 'bg-brand-secondary/20 border-brand-secondary/50'
                            : 'bg-white/5 border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 pr-10">
                            <div className="flex items-center gap-2">
                              {plan.serviceType === '4G Mobile' && (
                                <span
                                  title="Mobile (SIM)"
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-blue-500/20 text-blue-300 border border-blue-500/30"
                                >
                                  <Smartphone className="w-3 h-3" />
                                  Mobile
                                </span>
                              )}
                              {plan.serviceType === '4G Data' && (
                                <span
                                  title="Data (Router)"
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-teal-500/20 text-teal-300 border border-teal-500/30"
                                >
                                  <Router className="w-3 h-3" />
                                  Data
                                </span>
                              )}
                              <span className="text-white font-semibold">{plan.displayName || plan.planName}</span>
                              {plan.isPromoted && (
                                <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-brand-secondary/20 text-brand-secondary border border-brand-secondary/30">
                                  Popular
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                              {plan.downloadSpeed && <span>{plan.downloadSpeed} down</span>}
                              {plan.uploadSpeed && <span>{plan.uploadSpeed} up</span>}
                              {plan.dataAllowance && <span>• {plan.dataAllowance}</span>}
                            </div>
                            {plan.features && plan.features.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {plan.features.slice(0, 3).map((f: string, i: number) => (
                                  <span key={i} className="text-[10px] text-slate-500 bg-white/5 px-1.5 py-0.5 rounded">
                                    {f}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            {/* Plan prices grossed up to inc GST (sellPrice / setupFee stored ex GST) */}
                            <div className="text-brand-secondary font-bold text-lg">{formatCurrency(addGST(plan.sellPrice), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                            <div className="text-[10px] text-slate-400">/month inc GST</div>
                            {plan.setupFee ? (
                              <div className="text-[10px] text-slate-500">+ {formatCurrency(addGST(plan.setupFee), { minimumFractionDigits: 0, maximumFractionDigits: 0 })} setup inc GST</div>
                            ) : null}
                          </div>
                        </div>
                      </button>
                      {/* Edit button — shown on hover */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPlanToEdit(plan); setPlanModalOpen(true); }}
                        title="Edit this plan"
                        className="absolute top-2 right-2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white transition-all"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Custom Plan Name + Price (bundles, legacy/grandfathered, retention) —
              only shown once a plan is selected. Empty fields = use plan defaults. */}
          {isPlanBased && selectedPlan && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-3">
              <div>
                <h3 className="text-lg font-bold text-white">Custom name & pricing</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Optional — leave blank to use the plan's defaults. Use these for bundle plans,
                  legacy/grandfathered customers, or retention deals. Persists to the service
                  record so future invoices reflect exactly what's set here.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Custom plan name
                  </label>
                  <input
                    type="text"
                    value={customPlanName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomPlanName(e.target.value)}
                    placeholder={selectedPlan.displayName || selectedPlan.planName || 'e.g. Internet + VoIP Bundle'}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-brand-secondary transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Custom monthly price (AUD inc GST)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={customPrice}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomPrice(e.target.value)}
                      placeholder={selectedPlan.sellPrice != null ? exGstToIncString(selectedPlan.sellPrice, taxRate) : '0.00'}
                      aria-label="Custom monthly price including GST"
                      className="w-full pl-7 pr-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-brand-secondary transition-colors text-sm"
                    />
                  </div>
                  {/* Ex-GST shadow — typed inc GST, stored ex GST. */}
                  {!Number.isNaN(customPriceExGst) && customPriceExGst >= 0 && (
                    <p className="mt-1 text-[11px] text-slate-400">
                      Billed as {formatCurrency(customPriceExGst, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo ex GST
                    </p>
                  )}
                  {selectedPlan.sellPrice != null && (
                    <button
                      type="button"
                      onClick={() => setCustomPrice(exGstToIncString(selectedPlan.sellPrice, taxRate))}
                      className="mt-1 text-[11px] text-brand-secondary hover:text-brand-secondary/80 underline underline-offset-2 transition-colors"
                    >
                      Use plan price ({formatCurrency(addGST(selectedPlan.sellPrice, taxRate), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} inc GST)
                    </button>
                  )}
                </div>
              </div>

              {/* Reason — only meaningful when a custom price is entered */}
              {customPrice.trim() !== '' && selectedPlan.sellPrice != null
                && Math.round((customPriceExGst || 0) * 100) !== Math.round(selectedPlan.sellPrice * 100) && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Reason (saved to audit log)
                  </label>
                  <input
                    type="text"
                    value={customPriceReason}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomPriceReason(e.target.value)}
                    placeholder={customPlanName.trim()
                      ? `Bundle pricing — "${customPlanName.trim()}"`
                      : 'e.g. Bundle pricing, retention discount, legacy rate'}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-brand-secondary transition-colors text-sm"
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    Optional — defaults to a generic message. Stored in the service's price history so
                    we can always trace why this customer pays a non-standard rate.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Starlink Metadata */}
          {serviceType === 'Starlink' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Wifi className="w-5 h-5 text-brand-secondary" />
                Starlink Details
              </h3>

              {/* Model Selection — 2 toggle buttons */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Starlink Model
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setStarlinkModel('Standard')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      starlinkModel === 'Standard'
                        ? 'bg-indigo-600 text-white shadow-lg border-2 border-indigo-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Standard (Gen 3)
                  </button>
                  <button
                    type="button"
                    onClick={() => setStarlinkModel('Mini')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      starlinkModel === 'Mini'
                        ? 'bg-indigo-600 text-white shadow-lg border-2 border-indigo-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Starlink Mini
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Kit Number *
                  </label>
                  <input
                    type="text"
                    value={kitNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKitNumber(e.target.value)}
                    placeholder="e.g., UTxxxxxxxxxxxxxx-xxxxxxx"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Starlink Account Email
                  </label>
                  <input
                    type="email"
                    value={starlinkAccountEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStarlinkAccountEmail(e.target.value)}
                    placeholder="account@example.com"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>

              {/* Starlink Account Number */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Starlink Account Number
                </label>
                <input
                  type="text"
                  value={starlinkAccountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStarlinkAccountNumber(e.target.value)}
                  placeholder="ACC-XXXXXXX-XXXXX-XX"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors font-mono text-sm"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Searchable from /services and global search. Also auto-populated by Starlink billing emails.
                </p>
              </div>
            </div>
          )}

          {/* 4G/5G Metadata */}
          {serviceType === '4G/5G' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Wifi className="w-5 h-5 text-brand-secondary" />
                4G/5G Details
              </h3>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Carrier *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCarrier('Telstra')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      carrier === 'Telstra'
                        ? 'bg-blue-600 text-white shadow-lg border-2 border-blue-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Telstra
                  </button>
                  <button
                    type="button"
                    onClick={() => setCarrier('Optus')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      carrier === 'Optus'
                        ? 'bg-teal-600 text-white shadow-lg border-2 border-teal-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Optus
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    SIM Number *
                  </label>
                  <input
                    type="text"
                    value={simNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSimNumber(e.target.value)}
                    placeholder="Enter SIM number"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Service Number
                  </label>
                  <input
                    type="text"
                    value={serviceNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setServiceNumber(e.target.value)}
                    placeholder="e.g. 0402 729 756"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* NBN Extra Metadata */}
          {serviceType === 'NBN' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">NBN Account Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    NBN Service ID
                  </label>
                  <input
                    type="text"
                    value={nbnServiceId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNbnServiceId(e.target.value)}
                    placeholder="Service ID"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    NBN Location ID
                  </label>
                  <input
                    type="text"
                    value={nbnLocationId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNbnLocationId(e.target.value)}
                    placeholder="Location ID"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* OptiComm Extra Metadata */}
          {serviceType === 'OptiComm' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">OptiComm Account Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    OptiComm Service ID
                  </label>
                  <input
                    type="text"
                    value={opticommServiceId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOpticommServiceId(e.target.value)}
                    placeholder="Service / NID"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    OptiComm Order Number
                  </label>
                  <input
                    type="text"
                    value={opticommOrderNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOpticommOrderNumber(e.target.value)}
                    placeholder="Order number"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* WiFi Extension Metadata */}
          {serviceType === 'WiFi Extension' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">WiFi Extension Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Brand</label>
                  <select value={apBrand} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApBrand(e.target.value)} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors">
                    <option value="">Select brand</option>
                    <option value="UniFi">UniFi</option>
                    <option value="TP-Link Deco">TP-Link Deco</option>
                    <option value="Aruba">Aruba</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">AP Model</label>
                  <input type="text" value={apModel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApModel(e.target.value)} placeholder="e.g. U7-Pro" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">AP Count</label>
                  <input type="number" value={apCount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApCount(e.target.value)} placeholder="e.g. 3" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Switch Model</label>
                  <input type="text" value={switchModel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSwitchModel(e.target.value)} placeholder="e.g. USW-Lite-16-PoE" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">AP Serials (comma-separated)</label>
                  <input type="text" value={apSerials} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApSerials(e.target.value)} placeholder="e.g. AP001, AP002" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
              </div>
            </div>
          )}

          {/* Smart Home Metadata */}
          {serviceType === 'Smart Home' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">Smart Home Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Hub Brand</label>
                  <select value={hubBrand} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setHubBrand(e.target.value)} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors">
                    <option value="">Select brand</option>
                    <option value="Control4">Control4</option>
                    <option value="Savant">Savant</option>
                    <option value="Home Assistant">Home Assistant</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Hub Model</label>
                  <input type="text" value={hubModel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHubModel(e.target.value)} placeholder="e.g. EA-5" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Device Count</label>
                  <input type="number" value={deviceCount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeviceCount(e.target.value)} placeholder="e.g. 10" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Hub Serial</label>
                <input type="text" value={hubSerial} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHubSerial(e.target.value)} placeholder="Hub serial number" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
              </div>
            </div>
          )}

          {/* Security Metadata */}
          {serviceType === 'Security' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">Security System Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Camera Brand</label>
                  <select value={cameraBrand} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCameraBrand(e.target.value)} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors">
                    <option value="">Select brand</option>
                    <option value="UniFi Protect">UniFi Protect</option>
                    <option value="Hikvision">Hikvision</option>
                    <option value="Dahua">Dahua</option>
                    <option value="Reolink">Reolink</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Camera Model</label>
                  <input type="text" value={cameraModel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCameraModel(e.target.value)} placeholder="e.g. G5 Pro" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Camera Count</label>
                  <input type="number" value={cameraCount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCameraCount(e.target.value)} placeholder="e.g. 4" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">NVR Model</label>
                  <input type="text" value={nvrModel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNvrModel(e.target.value)} placeholder="e.g. UNVR Pro" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">NVR Serial</label>
                  <input type="text" value={nvrSerial} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNvrSerial(e.target.value)} placeholder="NVR serial number" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Doorbell Model</label>
                  <input type="text" value={doorbellModel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDoorbellModel(e.target.value)} placeholder="e.g. G4 Doorbell Pro" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Sensors</label>
                  <input type="number" value={sensorCount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSensorCount(e.target.value)} placeholder="e.g. 8" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Panel Serial</label>
                  <input type="text" value={panelSerial} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPanelSerial(e.target.value)} placeholder="Panel serial" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors" />
                </div>
              </div>
            </div>
          )}

          {/* VoIP Metadata */}
          {serviceType === 'VoIP' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Wifi className="w-5 h-5 text-brand-secondary" />
                VoIP Details
              </h3>

              {/* Sasboss + Main Number */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Sasboss Customer Name
                  </label>
                  <input
                    type="text"
                    value={sasbossCustomerName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSasbossCustomerName(e.target.value)}
                    placeholder="Customer name in Sasboss portal"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Main Business Number
                  </label>
                  <input
                    type="text"
                    value={mainNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMainNumber(e.target.value)}
                    placeholder="e.g. 02 4000 0000"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>

              {/* Complexity picker */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Call Flow Complexity
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setVoipComplexity('Simple')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      voipComplexity === 'Simple'
                        ? 'bg-emerald-600 text-white shadow-lg border-2 border-emerald-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Simple
                  </button>
                  <button
                    type="button"
                    onClick={() => setVoipComplexity('Complex')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      voipComplexity === 'Complex'
                        ? 'bg-emerald-600 text-white shadow-lg border-2 border-emerald-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Complex
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Simple = direct inbound/outbound. Complex = queues, auto-attendant, call groups, after-hours routing.
                </p>
              </div>

              {/* Handsets */}
              <div className="border-t border-white/10 pt-4">
                <label className="block text-sm font-semibold text-white mb-3">
                  Handsets
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Count On-Site</label>
                    <input
                      type="number"
                      value={handsetCount}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHandsetCount(e.target.value)}
                      placeholder="e.g. 4"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Extensions Configured</label>
                    <input
                      type="number"
                      value={extensionCount}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExtensionCount(e.target.value)}
                      placeholder="e.g. 6"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Handset Brand/Model</label>
                    <input
                      type="text"
                      value={handsetBrand}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHandsetBrand(e.target.value)}
                      placeholder="e.g. Yealink T31G"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs text-slate-400 mb-1">Handset Locations</label>
                  <textarea
                    value={handsetLocations}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setHandsetLocations(e.target.value)}
                    placeholder="e.g. Reception x2, warehouse x1, office x3"
                    rows={2}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-200 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={byoHandsets}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setByoHandsets(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 accent-brand-secondary"
                  />
                  Customer BYO handsets
                </label>
              </div>

              {/* Porting */}
              <div className="border-t border-white/10 pt-4">
                <label className="flex items-center gap-2 text-sm font-semibold text-white mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={portingRequired}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPortingRequired(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 accent-brand-secondary"
                  />
                  Number Porting Required
                </label>
                {portingRequired && (
                  <div className="space-y-3 pl-6">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Numbers to Port (comma or newline separated)</label>
                      <textarea
                        value={numbersToPort}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNumbersToPort(e.target.value)}
                        placeholder="02 4000 0000, 02 4000 0001"
                        rows={2}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Porting Status</label>
                      <select
                        value={portingStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPortingStatus(e.target.value as '' | 'pending' | 'submitted' | 'approved' | 'completed')}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors"
                      >
                        <option value="">Not started</option>
                        <option value="pending">Pending</option>
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Call flow features */}
              <div className="border-t border-white/10 pt-4">
                <label className="block text-sm font-semibold text-white mb-2">
                  Call Flow Notes
                </label>
                <textarea
                  value={callFlowNotes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCallFlowNotes(e.target.value)}
                  placeholder="Queues, ring groups, auto-attendant menu, after-hours routing, voicemail-to-email, etc."
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
                />
                <div className="flex flex-wrap gap-4 mt-3">
                  <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={voicemailEnabled}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVoicemailEnabled(e.target.checked)}
                      className="w-4 h-4 rounded border-white/20 bg-white/10 accent-brand-secondary"
                    />
                    Voicemail enabled
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoAttendant}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoAttendant(e.target.checked)}
                      className="w-4 h-4 rounded border-white/20 bg-white/10 accent-brand-secondary"
                    />
                    Auto-attendant / IVR
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Common Metadata */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
            <h3 className="text-lg font-bold text-white">Service Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Account Number
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountNumber(e.target.value)}
                  placeholder="Account #"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Contract End Date
                </label>
                <input
                  type="date"
                  value={contractEndDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContractEndDate(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
            </div>

            {/* Show selected plan summary — reflects custom overrides if entered */}
            {selectedPlan && (() => {
              const trimmedName = customPlanName.trim();
              // Ex-GST basis — customPrice is typed inc GST, sellPrice is stored ex GST.
              const parsedPrice = customPriceExGst;
              const effectiveName = trimmedName || selectedPlan.displayName || selectedPlan.planName;
              const effectivePrice = !Number.isNaN(parsedPrice) ? parsedPrice : selectedPlan.sellPrice;
              const isCustomName = !!trimmedName && trimmedName !== (selectedPlan.displayName || selectedPlan.planName);
              const isCustomPrice = !Number.isNaN(parsedPrice)
                && Math.round(parsedPrice * 100) !== Math.round(selectedPlan.sellPrice * 100);
              const isOverride = isCustomName || isCustomPrice;

              return (
                <div className={`p-3 rounded-lg border ${
                  isOverride
                    ? 'bg-amber-500/10 border-amber-500/40'
                    : 'bg-brand-secondary/10 border-brand-secondary/30'
                }`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-sm font-medium ${isOverride ? 'text-amber-300' : 'text-brand-secondary'}`}>
                      {isOverride ? 'Custom — will be saved' : 'Selected Plan'}
                    </span>
                    {isOverride && (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        Bundle / Custom
                      </span>
                    )}
                  </div>
                  <div className="text-white font-semibold">
                    {effectiveName}
                    {isCustomName && (
                      <span className="text-xs text-slate-400 font-normal ml-2">
                        (default: {selectedPlan.displayName || selectedPlan.planName})
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-300">
                    {/* effectivePrice / sellPrice stored ex GST — show inc GST to the office */}
                    {formatCurrency(addGST(effectivePrice ?? 0), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo inc GST
                    {isCustomPrice && (
                      <span className="text-xs text-slate-400 ml-2">
                        (plan price: {formatCurrency(addGST(selectedPlan.sellPrice), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} inc GST)
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}

            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Service Notes
              </label>
              <textarea
                value={notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
                placeholder="Any additional notes about this service..."
                rows={3}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddService}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-secondary to-yellow-600 hover:from-brand-secondary/90 hover:to-yellow-500 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isSubmitting ? 'Adding...' : 'Add Service'}
            </button>
          </div>
          </>)}
        </div>
      </div>
    </div>

    {/* Inline plan add/edit modal — stacks on top of this modal */}
    {planModalOpen && (
      <ServicePlanModal
        plan={planToEdit}
        onClose={() => { setPlanModalOpen(false); setPlanToEdit(null); }}
        onSuccess={(savedPlan) => {
          setPlanModalOpen(false);
          setPlanToEdit(null);
          if (savedPlan?.id) setSelectedPlanId(savedPlan.id);
        }}
        userId={localStorage.getItem('user_id') || 'system'}
        userName={localStorage.getItem('logged_in_user') || 'Admin'}
        tenantId={tenant?.id || getTenantIdSync()}
        defaultServiceType={
          serviceType === '4G/5G' ? '4G Mobile' : (serviceType as ServicePlan['serviceType'])
        }
      />
    )}
    </>
  );
}
